import React, { useState, useEffect } from 'react'
import { InputGroup, Input, Tag, Text, Button } from 'rsuite'
import SearchIcon from '@rsuite/icons/Search'
import SpinnerIcon from '@rsuite/icons/legacy/Spinner'
import { FormikProps } from 'formik'
import { List as VirtualizedList, AutoSizer } from 'react-virtualized'
import 'react-virtualized/styles.css'
import './TextInputWithSearchSuggestions.scss'
import { SectionLoader } from 'shared/atoms'

interface TextInputWithSearchSuggestionsProps<T> {
  label: string
  name: keyof T
  formikProps: FormikProps<T>
  searchResults: any[]
  onSearch: (searchTerm: string) => void
  placeholder?: string
  isLoading?: boolean
  isValuesFetching?: boolean
  onItemsRendered?: any
}

const TextInputWithSearchSuggestions = <T extends object>({
  label,
  name,
  formikProps,
  searchResults = [],
  onSearch,
  placeholder,
  isLoading,
  isValuesFetching,
  onItemsRendered
}: TextInputWithSearchSuggestionsProps<T>) => {
  const [searchTerm, setSearchTerm] = useState<string>('')
  const defaultSelctedItems = formikProps.values[name]
  const [selectedItems, setSelectedItems] = useState<(string | number)[]>(
    defaultSelctedItems ? (defaultSelctedItems as (string | number)[]) : []
  )

  useEffect(() => {
    // Reseting selected items if the form has been reset using "clear all filters" button
    if (!defaultSelctedItems) {
      setSelectedItems([])
    }
  }, [defaultSelctedItems])

  const [showSelectedItems, setShowSelectedItems] = useState<boolean>(false)
  const handleInputChange = (value: string) => {
    setSearchTerm(value)
    onSearch(value)
  }

  const handleSelect = (item: string | number) => {
    if (!selectedItems.includes(item)) {
      const updatedItems = [...selectedItems, item]
      setSelectedItems(updatedItems)
      formikProps.setFieldValue(name as string, updatedItems)
    }
  }

  const handleRemove = (item: string | number, event: React.MouseEvent) => {
    event.preventDefault()
    const updatedItems = selectedItems.filter((i) => i !== item)
    setSelectedItems(updatedItems)
    formikProps.setFieldValue(name as string, updatedItems)
  }

  const renderSelectedItems = () => (
    <div className="selected-tags">
      {selectedItems.map((item) => (
        <Tag key={item} closable onClose={(event) => handleRemove(item, event)}>
          {item}
        </Tag>
      ))}
    </div>
  )

  const filteredResults = searchResults.filter(
    (item) => !selectedItems.includes(item)
  )

  const rowRenderer = ({ key, index, style }: any) => {
    const item = filteredResults[index]
    return (
      <div key={key} style={style}>
        <Button
          onClick={() => handleSelect(item)}
          className="search-result-item"
        >
          {index === filteredResults.length - 1 && isValuesFetching ? (
            <span>
              <SpinnerIcon spin /> Loading...
            </span>
          ) : (
            item
          )}
        </Button>
      </div>
    )
  }

  return (
    <div className="filterDescription">
      {label && (
        <Text size="md" weight="bold">
          {label}
        </Text>
      )}

      <InputGroup inside className="filterInputGroup">
        <InputGroup.Button>
          <SearchIcon />
        </InputGroup.Button>
        <Input
          placeholder={placeholder ?? `Enter ${label.toLowerCase()}`}
          value={searchTerm}
          onChange={handleInputChange}
        />
      </InputGroup>

      <Button
        className="selected-count"
        onClick={() => setShowSelectedItems(!showSelectedItems)}
      >
        {selectedItems.length}{' '}
        {selectedItems.length > 1 && !label.endsWith('s') ? `${label}s` : label}{' '}
        selected
      </Button>

      {showSelectedItems && renderSelectedItems()}

      {isLoading ? (
        <SectionLoader message="Loading, please wait..." />
      ) : (
        <>
          {filteredResults.length > 0 && (
            <div className="search-results">
              <AutoSizer disableHeight>
                {({ width }) => (
                  <VirtualizedList
                    width={width}
                    height={250}
                    rowCount={filteredResults.length}
                    rowHeight={40}
                    rowRenderer={rowRenderer}
                    onRowsRendered={({ stopIndex }: { stopIndex: number }) =>
                      onItemsRendered?.({
                        visibleStopIndex:
                          stopIndex + (selectedItems.length ?? 0)
                      })
                    }
                  />
                )}
              </AutoSizer>
            </div>
          )}
          {filteredResults.length === 0 && (
            <div className="no-results">No results found</div>
          )}
        </>
      )}
    </div>
  )
}

export default TextInputWithSearchSuggestions
